import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Loader2, RefreshCw, Globe, Server, ShieldCheck, AlertTriangle, CheckCircle2, Check, CheckCheck, Mail } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";

const TYPE_ICON = { framework: Globe, hosting: Server, security: ShieldCheck, reachability: AlertTriangle };
const SEV_COLOR = { info: "#4da3ff", warning: "#FFD60A", critical: "#FF3B30" };
const FILTERS = ["all", "critical", "warning", "info"];
const TYPES = ["all", "framework", "hosting", "security", "reachability"];

export default function Changes() {
  const navigate = useNavigate();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [domainFilter, setDomainFilter] = useState("all");
  const [prefs, setPrefs] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/changes");
      setChanges(res.data.changes || []);
    } catch { toast.error("Failed to load change log"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/me/email-prefs").then((r) => setPrefs(r.data)).catch(() => {});
  }, []);

  const toggleDigest = async (checked) => {
    // checked = digest ON → opt_out = !checked
    setSavingPrefs(true);
    setPrefs((p) => ({ ...p, digest_opt_out: !checked }));
    try {
      await api.patch("/me/email-prefs", { digest_opt_out: !checked });
      toast.success(checked ? "Weekly digest on" : "Weekly digest off");
    } catch {
      toast.error("Couldn't update preference");
      setPrefs((p) => ({ ...p, digest_opt_out: checked }));
    } finally { setSavingPrefs(false); }
  };

  const notifyBadge = () => window.dispatchEvent(new Event("changes-updated"));

  const ack = async (id) => {
    setChanges((prev) => prev.map((c) => (c.change_id === id ? { ...c, read: true } : c)));
    try { await api.post(`/changes/${id}/ack`); notifyBadge(); }
    catch { toast.error("Failed to acknowledge"); load(); }
  };

  const ackAll = async () => {
    setChanges((prev) => prev.map((c) => ({ ...c, read: true })));
    try { await api.post("/changes/read"); notifyBadge(); toast.success("All alerts acknowledged"); }
    catch { toast.error("Failed"); load(); }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/scan/refresh-all");
      toast.success(`Re-scanned ${res.data.rescanned} domain(s) · ${res.data.changes} change(s) detected`);
      await load();
      notifyBadge();
    } catch { toast.error("Re-scan failed"); }
    finally { setRefreshing(false); }
  };

  const counts = {
    all: changes.length,
    critical: changes.filter((c) => c.severity === "critical").length,
    warning: changes.filter((c) => c.severity === "warning").length,
    info: changes.filter((c) => c.severity === "info").length,
  };
  const unread = changes.filter((c) => !c.read).length;
  const domainOptions = [...new Set(changes.map((c) => c.domain))].sort();
  const visible = changes.filter(
    (c) =>
      (filter === "all" || c.severity === filter) &&
      (typeFilter === "all" || c.type === typeFilter) &&
      (domainFilter === "all" || c.domain === domainFilter)
  );

  return (
    <Layout>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="flex items-center gap-2 text-[#4da3ff] mb-2">
            <Bell size={16} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Change Log</span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-white">Alerts & Change Detection</h1>
          <p className="text-zinc-400 mt-2">Automatic nightly re-scans (03:00 UTC) track framework, hosting, security and uptime changes.</p>
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button
              data-testid="ack-all-btn"
              onClick={ackAll}
              className="bg-transparent border border-white/20 text-white hover:bg-white/10 rounded-lg px-4 py-2.5 text-sm transition-colors flex items-center gap-2"
            >
              <CheckCheck size={16} /> Mark all read
            </button>
          )}
          <button
            data-testid="refresh-all-btn"
            onClick={refreshAll}
            disabled={refreshing}
            className="btn-brand disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2"
          >
            {refreshing ? <><Loader2 size={16} className="animate-spin" /> Re-scanning…</> : <><RefreshCw size={16} /> Re-scan all now</>}
          </button>
        </div>
      </div>

      {/* Weekly email digest preference */}
      {prefs && (
        <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#121212] px-4 py-3 mb-5" data-testid="digest-prefs">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-[#4da3ff]/10 border border-[#4da3ff]/30">
            <Mail size={16} className="text-[#4da3ff]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-white text-sm font-medium">Weekly email digest</p>
            <p className="text-zinc-500 text-xs mt-0.5">
              A Monday summary of what changed on your sites, sent to {prefs.email}.
              {!prefs.email_configured && <span className="text-[#FFD60A]"> (Email delivery isn't configured yet.)</span>}
            </p>
          </div>
          <Switch
            data-testid="digest-toggle"
            checked={!prefs.digest_opt_out}
            disabled={savingPrefs}
            onCheckedChange={toggleDigest}
          />
        </div>
      )}

      {/* Severity filter */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="severity-filter">
        {FILTERS.map((f) => {
          const active = filter === f;
          const color = f === "all" ? "#4da3ff" : SEV_COLOR[f];
          return (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-mono capitalize transition-colors border ${
                active ? "text-white" : "text-zinc-400 hover:text-white border-white/10 hover:border-white/25"
              }`}
              style={active ? { background: `${color}1f`, borderColor: `${color}66`, color } : {}}
            >
              {f} <span className="opacity-70">({counts[f]})</span>
            </button>
          );
        })}
      </div>

      {/* Type + domain filter */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="type-filter">
        {TYPES.map((t) => {
          const active = typeFilter === t;
          const Icon = t === "all" ? null : TYPE_ICON[t];
          return (
            <button
              key={t}
              data-testid={`type-${t}`}
              onClick={() => setTypeFilter(t)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs capitalize transition-colors border ${
                active ? "bg-white/10 text-white border-white/25" : "text-zinc-400 hover:text-white border-white/10 hover:border-white/20"
              }`}
            >
              {Icon && <Icon size={13} />} {t}
            </button>
          );
        })}
        {domainOptions.length > 0 && (
          <select
            data-testid="domain-filter"
            value={domainFilter}
            onChange={(e) => setDomainFilter(e.target.value)}
            className="ml-auto bg-black border border-white/20 rounded-md px-3 py-1.5 text-xs font-mono text-white focus:border-[#007AFF] outline-none"
          >
            <option value="all">All domains ({domainOptions.length})</option>
            {domainOptions.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#007AFF]" /></div>
      ) : visible.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-white/10 rounded-xl">
          <CheckCircle2 size={40} className="text-[#32D74B] mx-auto" />
          <p className="text-zinc-300 mt-4">{changes.length === 0 ? "No changes detected." : "No alerts match these filters."}</p>
          <p className="text-zinc-600 text-sm mt-1">Your domains are stable. Run a manual re-scan to check now.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="changes-list">
          {visible.map((c) => {
            const Icon = TYPE_ICON[c.type] || Bell;
            const color = SEV_COLOR[c.severity] || "#4da3ff";
            return (
              <div
                key={c.change_id}
                data-testid={`change-item-${c.change_id}`}
                className={`flex items-center gap-4 bg-[#121212] border rounded-lg p-4 transition-colors ${
                  c.read ? "border-white/5 opacity-55" : "border-white/10 hover:border-white/25"
                }`}
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a`, border: `1px solid ${color}44` }}>
                  <Icon size={16} style={{ color }} />
                </div>
                <div className="min-w-0 flex-1 cursor-pointer" onClick={() => navigate(`/domain/${encodeURIComponent(c.domain)}`)}>
                  <p className="text-white text-sm font-medium truncate">{c.message}</p>
                  <p className="font-mono text-xs text-zinc-500 mt-0.5">{c.domain} · {new Date(c.created_at).toLocaleString()}</p>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded" style={{ color, background: `${color}1a` }}>
                  {c.severity}
                </span>
                {c.read ? (
                  <span className="text-zinc-600 text-xs font-mono flex items-center gap-1 w-[104px] justify-end"><Check size={13} /> acknowledged</span>
                ) : (
                  <button
                    data-testid={`ack-${c.change_id}`}
                    onClick={() => ack(c.change_id)}
                    className="w-[104px] justify-center bg-white/5 border border-white/15 text-white hover:bg-[#007AFF] hover:border-[#007AFF] rounded-md px-3 py-1.5 text-xs transition-colors flex items-center gap-1.5"
                  >
                    <Check size={13} /> Acknowledge
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
